import React, { useEffect, useState } from 'react';
import './InvestingPage.css';
import './EtpPricesPage.css';

const BUCKET_LABELS = {
  commodity_broad: 'Commodities (broad)',
  energy: 'Energy',
  precious_metals: 'Precious metals',
  industrial_metals: 'Industrial metals',
  agriculture: 'Agriculture',
  livestock: 'Livestock',
  gold: 'Gold',
};

function fmtBucket(value) {
  if (!value) return '—';
  return BUCKET_LABELS[value] || value.replace(/_/g, ' ');
}

function fmtNum(value, digits = 2) {
  if (value == null || Number.isNaN(value)) return '—';
  return Number(value).toFixed(digits);
}

function fmtRefresh(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function positioningClass(slug) {
  if (!slug) return '';
  if (slug.includes('extreme_spec_long')) return 'cot-pos-extreme-long';
  if (slug.includes('elevated')) return 'cot-pos-elevated';
  if (slug.includes('extreme_spec_short')) return 'cot-pos-extreme-short';
  if (slug.includes('depressed')) return 'cot-pos-depressed';
  return 'cot-pos-neutral';
}

function indexFillPct(index) {
  if (index == null || Number.isNaN(index)) return 0;
  return Math.min(100, Math.max(0, Number(index)));
}

export default function EtcCotBucketPanel({ selectedBucket = null, onBucketSelect = null }) {
  const [items, setItems] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch('/api/investing/etc/cot-buckets');
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || body.hint || res.statusText);
        }
        const data = await res.json();
        if (!cancelled) {
          setItems(data.items || []);
          setFetchedAt(data.fetched_at);
          setNote(data.note || '');
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || String(e));
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const refreshLabel = fmtRefresh(fetchedAt);

  return (
    <div className="etc-cot-panel">
      <div className="etc-cot-panel-head">
        <div>
          <h3 className="etc-cot-panel-title">CFTC managed-money positioning</h3>
          <p className="etc-cot-panel-hint">
            Weekly futures-only disaggregated COT by commodity bucket. Click a bucket to filter listings.
          </p>
        </div>
        {refreshLabel && (
          <span className="etc-cot-refresh" title={fetchedAt}>
            Updated {refreshLabel}
          </span>
        )}
      </div>

      {note && (
        <details className="etc-cot-method">
          <summary>Methodology</summary>
          <p className="etc-cot-note">{note}</p>
        </details>
      )}

      {error && (
        <div className="investing-error">
          {error}
          {' '}
          Run Flyway V003 and investing backend with
          {' '}
          <code>--cot-buckets-only --force-cot-buckets</code>
          .
        </div>
      )}

      <div className="etc-cot-cards" role="list">
        {loading && (
          <div className="etc-cot-cards-empty" role="status">Loading COT buckets…</div>
        )}
        {!loading && items.length === 0 && !error && (
          <div className="etc-cot-cards-empty">No COT bucket data yet.</div>
        )}
        {!loading && items.map((row) => {
          const selected = selectedBucket === row.bucket;
          const CardTag = onBucketSelect ? 'button' : 'div';
          const cardProps = onBucketSelect
            ? {
              type: 'button',
              onClick: () => onBucketSelect(row.bucket),
              title: 'Filter ETC listings by this bucket',
            }
            : {};

          return (
            <CardTag
              key={row.bucket}
              role="listitem"
              className={`etc-cot-card${selected ? ' etc-cot-card-selected' : ''}`}
              {...cardProps}
            >
              <div className="etc-cot-card-top">
                <span className="etc-cot-card-bucket">{fmtBucket(row.bucket)}</span>
                <span className="etc-cot-card-index">{fmtNum(row.cot_index, 0)}</span>
              </div>
              <div className="etc-cot-card-bar" aria-hidden>
                <span
                  className="etc-cot-card-bar-fill"
                  style={{ width: `${indexFillPct(row.cot_index)}%` }}
                />
              </div>
              <div className="etc-cot-card-meta">
                <span title="3y rolling z-score">z {fmtNum(row.cot_z, 2)}</span>
                <span
                  className={`cot-pos-badge ${positioningClass(row.positioning_read)}`}
                  title={row.positioning_read}
                >
                  {row.positioning_label || row.positioning_read || '—'}
                </span>
              </div>
              <div className="etc-cot-card-dates">
                Report {row.report_date || '—'}
                {' · '}
                As of {row.available_date || '—'}
              </div>
            </CardTag>
          );
        })}
      </div>
    </div>
  );
}
